// Updated api.ts functions to handle long-running requests with Ably
import {
  CourseMessageRequest,
  EditedMessagesRequest,
  SendMessageRequest,
  TTSRequest,
} from '../views/userChat/messages.types';

import ablyClientManager from '../utils/ablyClientManager';

// Type definitions for our mock response
interface MockResponse {
  ok: boolean;
  status: number;
  statusText: string;
  json: () => Promise<any>;
  text: () => Promise<string>;
  arrayBuffer?: () => Promise<ArrayBuffer>;
  blob?: () => Promise<Blob>;
  formData?: () => Promise<FormData>;
  headers: {
    get: (name: string) => string | null;
  };
}

const proxyURL = '/.netlify/functions/api-proxy';
const backgroundProxyURL = '/.netlify/functions/api-proxy-background';
const enableDevTools = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';

// Generate unique job ID
const generateJobId = () => {
  return `job-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Generate channel name for job
const generateChannelName = (endpoint, jobId) => {
  // Clean endpoint name for channel
  const cleanEndpoint = endpoint.replace(/[^a-zA-Z0-9]/g, '-');
  return `background-jobs:${cleanEndpoint}:${jobId}`;
};

// Utility function for long-running requests with Ably communication
const makeLongRunningRequest = async (
  endpoint: string,
  method: string = 'POST',
  data?: any,
  userToken: string = '',
  authUserId?: string,
  onProgress?: (message: string) => void
): Promise<Response | MockResponse> => {
  const longRunningEndpoints = [
    'chat',
    'courses/instruct',
    'courses/index_and_generate_curriculum',
    'index_all',
    'google-drive/sync-google-drive-folder',
    'journal/chat'
  ];

  // More precise endpoint matching to avoid false positives
  const isLongRunning = longRunningEndpoints.some(longEndpoint => {
    // Exact match
    if (endpoint === longEndpoint) return true;

    // For endpoints that should match with query parameters
    if (endpoint.startsWith(longEndpoint + '?')) return true;

    // For endpoints that should match with path parameters
    if (longEndpoint.includes('/') && endpoint.startsWith(longEndpoint + '/')) return true;

    // Special case: 'chat' should not match 'chat_messages' or other chat-related endpoints
    if (longEndpoint === 'chat') {
      return endpoint === 'chat' || endpoint.startsWith('chat?');
    }

    return false;
  });

  // For non-long-running requests, use the regular proxy
  if (!isLongRunning) {
    return makeProxyRequest(endpoint, method, data, userToken, authUserId);
  }

  // For long-running requests, use background processing with Ably
  try {
    // Generate job ID and channel name
    const jobId = generateJobId();
    const channelName = generateChannelName(endpoint, jobId);

    if (enableDevTools) console.log(`Starting long-running request for ${endpoint} with jobId: ${jobId}, channel: ${channelName}`);

    // Get the channel from our client manager
    const channel = await ablyClientManager.getChannel(channelName);

    let progressCallback: ((message: any) => void) | null = null;
    let completeCallback: ((message: any) => void) | null = null;

    // Create promise that resolves when the background job completes
    const backgroundPromise = new Promise<MockResponse>((resolve, reject) => {
      // Handle progress messages
      progressCallback = (message: any) => {
        if (message.data.jobId === jobId) {
          if (enableDevTools) console.log('Progress update:', message.data.message);
          if (onProgress) {
            onProgress(message.data.message);
          }
        }
      };

      // Handle completion messages
      completeCallback = (message: any) => {
        if (message.data.jobId === jobId) {
          if (enableDevTools) console.log('Job completed:', message.data);

          // Clean up subscriptions
          channel.unsubscribe('progress', progressCallback);
          channel.unsubscribe('complete', completeCallback);

          if (message.data.status === 'success') {
            // Create a mock response object that matches the expected format
            const mockResponse: MockResponse = {
              ok: true,
              status: message.data.statusCode || 200,
              statusText: 'OK',
              json: () => Promise.resolve(message.data.data),
              text: () => Promise.resolve(JSON.stringify(message.data.data)),
              headers: {
                get: (name: string) => {
                  if (name.toLowerCase() === 'content-type') {
                    return 'application/json';
                  }
                  return null;
                }
              }
            };
            resolve(mockResponse);
          } else {
            reject(new Error(message.data.message || message.data.error || 'Background job failed'));
          }
        }
      };

      // Subscribe to channels
      channel.subscribe('progress', progressCallback);
      channel.subscribe('complete', completeCallback);

      // Set a timeout for the entire operation (e.g., 10 minutes)
      setTimeout(() => {
        // Clean up subscriptions
        if (progressCallback) channel.unsubscribe('progress', progressCallback);
        if (completeCallback) channel.unsubscribe('complete', completeCallback);
        reject(new Error('Background job timed out after 10 minutes'));
      }, 10 * 60 * 1000); // 10 minutes
    });

    // Start the background job (fire and forget)
    const requestBody = {
      endpoint,
      method,
      data,
      userToken,
      authUserId,
      jobId,
      ablyChannel: channelName
    };

    try {
      const backgroundResponse = await fetch(backgroundProxyURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      // We only care if the request was accepted, not the response content
      if (!backgroundResponse.ok) {
        // Clean up subscriptions if background job failed to start
        if (progressCallback) channel.unsubscribe('progress', progressCallback);
        if (completeCallback) channel.unsubscribe('complete', completeCallback);

        throw new Error(`Failed to start background job: HTTP ${backgroundResponse.status}`);
      }

      if (enableDevTools) console.log(`Background job initiated for ${endpoint} with jobId: ${jobId}`);

    } catch (fetchError) {
      // Clean up subscriptions if we couldn't even start the background job
      if (progressCallback) channel.unsubscribe('progress', progressCallback);
      if (completeCallback) channel.unsubscribe('complete', completeCallback);

      console.error('Failed to initiate background job:', fetchError);
      throw new Error(`Failed to initiate background job: ${fetchError.message}`);
    }

    if (onProgress) {
      onProgress('Background processing started...');
    }

    // Wait for the background job to complete via Ably
    return await backgroundPromise;

  } catch (error) {
    console.error('Long-running request error:', error);

    // Fallback to regular proxy for backward compatibility
    if (enableDevTools) console.log('Falling back to regular proxy due to error:', error.message);
    if (onProgress) {
      onProgress('Falling back to standard processing...');
    }

    return makeProxyRequest(endpoint, method, data, userToken, authUserId);
  }
};

// Helper function to make proxy requests with timeout handling
const makeProxyRequest = async (
  endpoint: string,
  method: string = 'GET',
  data?: any,
  userToken: string = '',
  authUserId?: string
): Promise<Response> => {
  const requestBody = {
    endpoint,
    method,
    data,
    userToken,
    authUserId,
  };

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody)
  };

  try {
    const response = await fetch(proxyURL, requestOptions);

    // Handle timeout responses specifically
    if (response.status === 408) {
      const errorData = await response.json();
      console.warn(`Request timeout for ${endpoint}:`, errorData.message);

      // You can customize this error handling based on your UI needs
      throw new Error(`Request timed out: ${errorData.message}`);
    }

    return response;
  } catch (error) {
    // If it's a fetch error (network issues, etc.)
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error - please check your connection and try again');
    }

    // Re-throw other errors as-is
    throw error;
  }
};

// Export the updated functions with proper typing
export { makeLongRunningRequest, makeProxyRequest };

// Type guard to check if response is our mock response
export const isMockResponse = (response: Response | MockResponse): response is MockResponse => {
  return 'headers' in response && typeof response.headers.get === 'function' && !('url' in response);
};

// Helper function to safely handle both Response and MockResponse
export const handleResponse = async (response: Response | MockResponse) => {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }
  return response.json();
};

export interface DeleteDataRequest {
  user_name?: string;
  user_id?: string;
  delete_profile?: boolean;
  delete_index?: boolean;
  delete_conversation?: DeleteConversationRequest;
  delete_notifications?: boolean;
  delete_training_info?: boolean;
  delete_documents?: boolean;
  delete_webpages?: boolean;
}

export interface DeleteConversationRequest {
  creator_id: string;
}

// Voice functions - keeping direct calls since they handle binary data/audio
export function cloneVoice(userId: string, userToken: string = ''): any {
  const data = { user_id: userId };
  return makeProxyRequest('voices/clone', 'POST', data, userToken);
}

export function deleteVoice(userId: string, userToken: string = ''): any {
  const data = { user_id: userId };
  return makeProxyRequest('voices/delete', 'POST', data, userToken);
}

// Alternative streaming TTS function - more efficient for larger audio files
export function getTextToSpeechStreaming(data: TTSRequest, userToken: string = ''): any {
  const proxyURL = '/.netlify/functions/tts-streaming-proxy';

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      data: data,
      userToken: userToken
    })
  };

  const audioCtx = new AudioContext();

  return fetch(proxyURL, requestOptions)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`TTS Streaming Proxy error, status = ${response.status}`);
      }
      return response.arrayBuffer();
    })
    .then((buffer) => audioCtx.decodeAudioData(buffer))
    .then((decodedData) => {
      const source = audioCtx.createBufferSource();
      const analyzer = audioCtx.createAnalyser();
      const gainNode = audioCtx.createGain();
      gainNode.gain.value = 1;
      source.buffer = decodedData;
      source.connect(analyzer);
      analyzer.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      return [source, analyzer, gainNode];
    });
}

// Updated main TTS function with emoji removal moved to server-side
export function getTextToSpeech(data: TTSRequest, userToken: string = ''): any {
  const proxyURL = '/.netlify/functions/tts-proxy';

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      data: data, // No need to remove emojis client-side anymore
      userToken: userToken
    })
  };

  const audioCtx = new AudioContext();

  return fetch(proxyURL, requestOptions)
    .then(async (response) => {
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`TTS Proxy error: ${errorData.error || response.statusText}`);
      }

      const responseData = await response.json();

      if (!responseData.success || !responseData.audioData) {
        throw new Error('Invalid TTS response format');
      }

      // Convert base64 back to ArrayBuffer
      const binaryString = atob(responseData.audioData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      return bytes.buffer;
    })
    .then((buffer) => audioCtx.decodeAudioData(buffer))
    .then((decodedData) => {
      const source = audioCtx.createBufferSource();
      const analyzer = audioCtx.createAnalyser();
      const gainNode = audioCtx.createGain();
      gainNode.gain.value = 1;
      source.buffer = decodedData;
      source.connect(analyzer);
      analyzer.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      return [source, analyzer, gainNode];
    });
}

export async function getMessageHistory(
  authUserId: string,
  roomId: string,
  page: number = 1,
  pageSize: number = 100,
  userToken: string = ''
) {
  const requestBody = {
    endpoint: `user/chat_messages?room_id=${roomId}&page=${page}&pageSize=${pageSize}`,
    method: 'GET',
    authUserId: authUserId,
    userToken: userToken,
  };

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody)
  };

  return fetch(proxyURL, requestOptions);
}

export function sendMessageToSmartAssistant(
  data: SendMessageRequest,
  userType: string,
  userToken: string = '',
  onProgress?: (message: string) => void
): any {
  return makeLongRunningRequest('chat', 'POST', data, userToken, undefined, onProgress);
}

export function buildPersonality(documentId: string, userToken: string = ''): any {
  const data = { doc_id: '' + documentId + '' };
  return makeProxyRequest('indexer', 'POST', data, userToken);
}

export function editedMessageResponse(data: EditedMessagesRequest, userToken: string = ''): any {
  return makeProxyRequest('insert_message', 'POST', data, userToken);
}

export async function updatePersona(
  userId: string,
  data: { [key: string]: boolean },
  userToken: string = '',
  onProgress?: (message: string) => void
): Promise<any> {
  const endpoint = 'index_all';
  const jsonObject = {
    user_id: userId,
    ...data,
  };

  return makeLongRunningRequest(endpoint, 'POST', jsonObject, userToken, undefined, onProgress);
}

export function deleteData(data: DeleteDataRequest, userToken: string = ''): any {
  return makeProxyRequest('delete_data', 'POST', data, userToken);
}

export function clearChat(roomId: string, userToken: string = ''): any {
  const endpoint = `messages/clear_chat?room_id=${roomId}`;
  return makeProxyRequest(endpoint, 'DELETE', undefined, userToken);
}

export async function getProfileByUserId(
  authUserId: string,
  userId: string,
  get_details: boolean = false,
  get_top_streaks: boolean = false,
  userToken: string = ''
) {
  const endpoint = `user/profile?user_id=${userId}&get_details=${get_details}&get_top_streaks=${get_top_streaks}`;

  // For profile requests, we might need the authUserId in headers
  const requestBody = {
    endpoint,
    method: 'GET',
    authUserId: authUserId,
    userToken: userToken,
  };

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody)
  };

  return fetch(proxyURL, requestOptions);
}

export async function getProfileByUsername(
  authUserId: string,
  userName: string,
  get_details: boolean = false,
  get_top_streaks: boolean = false,
  userToken: string = ''
) {
  const endpoint = `user/profile_by_username?user_name=${userName}&get_details=${get_details}&get_top_streaks=${get_top_streaks}`;

  const requestBody = {
    endpoint,
    method: 'GET',
    authUserId: authUserId,
    userToken: userToken,
  };

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody)
  };

  return fetch(proxyURL, requestOptions);
}

export function deleteFromPersona(
  userId: string,
  llama_index_doc_id: string,
  userToken: string = ''
): any {
  const data = {
    user_id: userId,
    llama_index_doc_id: llama_index_doc_id,
  };

  return makeProxyRequest('delete_from_index', 'POST', data, userToken);
}

export async function fetchLevelQuestionsData(queryParams: any, userToken: string = ''): Promise<any> {
  try {
    const creatorId = queryParams?.queryKey?.[1];
    const levelId = queryParams?.queryKey?.[2];
    const data = {
      user_id: creatorId,
      level: levelId,
    };

    const response = await makeProxyRequest('get_training_questions', 'POST', data, userToken);
    return response?.json();
  } catch (err) {
    console.log('fetch-level-data-questions-error', err);
    throw err;
  }
}

export function reportPost(
  userId: string,
  postId: string,
  postContent: string = "",
  userToken: string = ''
): Promise<any> {
  const data = { userId, postId, postContent };

  return makeProxyRequest('report-post', 'POST', data, userToken)
    .then(async (resp) => {
      if (!resp.ok) {
        const err = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${err}`);
      }
      return resp.json();
    });
}

export function reportComment(
  userId: string,
  postId: string,
  commentId: string,
  commentContent: string = "",
  userToken: string = ''
): Promise<any> {
  const data = { userId, postId, commentId, commentContent };

  return makeProxyRequest('report-comment', 'POST', data, userToken)
    .then(async (resp) => {
      if (!resp.ok) {
        const err = await resp.text();
        throw new Error(`HTTP ${resp.status}: ${err}`);
      }
      return resp.json();
    });
}

export async function updateLevelQuestionsData(
  updatedLevelData: any,
  userToken: string = ''
): Promise<any> {
  try {
    const response = await makeProxyRequest('update_training_questions', 'POST', updatedLevelData, userToken);
    return response?.json();
  } catch (err) {
    console.log('update-level-data-questions-error', err);
    throw err;
  }
}

export async function fetchCreatorLevelInfo(queryParams: any, userToken: string = ''): Promise<any> {
  try {
    const creatorId = queryParams?.queryKey?.[1];
    const data = { user_id: creatorId };

    const response = await makeProxyRequest('get_train_level_info', 'POST', data, userToken);
    return response?.json();
  } catch (err) {
    console.log('fetch-level-questions-error', err);
  }
}

export function uploadFacebookAuthResponse(response: any, userToken: string = ''): any {
  try {
    return makeProxyRequest('auth/facebook', 'POST', response, userToken);
  } catch (err) {
    console.log('Facebook-authentication-error', err);
  }
}

export function getNotifications(userId: string, page?: number, pageSize?: number, userToken: string = ''): any {
  const data = {
    user_id: userId,
    page: page,
    pageSize: pageSize,
  };

  return makeProxyRequest('notifications', 'POST', data, userToken);
}

export function markNotificationRead(
  userId: string,
  notificationId: string,
  userToken: string = ''
): any {
  const data = {
    user_id: userId,
    notification_id: notificationId,
  };

  return makeProxyRequest('notifications', 'PUT', data, userToken);
}

export function getSortedCreatorList(
  userId: string,
  page?: number,
  pageSize?: number,
  sortBy?: string,
  userToken: string = ''
): any {
  page ||= 1;
  pageSize ||= 10;
  sortBy ||= 'oldestFirst';
  const endpoint = `creators/${userId}?page=${page}&pageSize=${pageSize}&sortBy=${sortBy}`;

  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export function getIntroMessage(userId: string, userToken: string = ''): any {
  const endpoint = `generate_intro?user_id=${userId}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export function getCreatorsList(
  userId: string,
  searchQuery: string,
  page?: number,
  pageSize?: number,
  userToken: string = ''
): any {
  page ||= 1;
  pageSize ||= 10;
  const endpoint = `search/${userId}?page=${page}&pageSize=${pageSize}&query=${searchQuery}`;

  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export function getCorporateAccounts(
  enterpriseName: string,
  page = 1,
  pageSize = 10,
  sortBy = 'aToz',
  userToken: string = ''
) {
  const endpoint = `/corporate_accounts/${enterpriseName}?page=${page}&pageSize=${pageSize}&sortBy=${sortBy}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export function sendEmailWithTemplate(email: string, userToken: string = '') {
  const data = { email: email };

  return makeProxyRequest('/send-email-with-template', 'POST', data, userToken)
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      console.log('Email sent successfully:', data);
      return data;
    })
    .catch((error) => {
      console.error('Error sending email:', error);
    });
}

////////////////////////////////////////////////////////////////////
// Course related functions
////////////////////////////////////////////////////////////////////

export function searchCoursesList(
  searchQuery: string,
  page?: number,
  pageSize?: number,
  userToken: string = ''
) {
  page ||= 1;
  pageSize ||= 10;
  const endpoint = `courses/search?page=${page}&pageSize=${pageSize}&query=${searchQuery}`;

  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export function getSortedCoursesList(
  page?: number,
  pageSize?: number,
  filterBy?: string,
  userToken: string = ''
) {
  page ||= 1;
  pageSize ||= 10;
  filterBy ||= 'None';
  const endpoint = `courses/search?page=${page}&pageSize=${pageSize}&filterBy=${filterBy}`;

  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export function getCourseInfo(courseId: string, userToken: string = '') {
  const endpoint = `courses/?course_id=${courseId}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export const createCourseDoc = async (
  userId: string,
  courseTitle: string,
  subject: string,
  grade: string,
  coursePrompt: string,
  numModules: number,
  teachingTone: string,
  userToken: string = ''
) => {
  const data = {
    course_title: courseTitle,
    user_id: userId,
    user_description: coursePrompt,
    subject: subject,
    grade: grade,
    module_length: numModules,
    teaching_tone: teachingTone,
  };

  try {
    const response = await makeProxyRequest('courses/', 'POST', data, userToken);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error('Error in createCurriculum API call:', error);
    throw error;
  }
};

export function generateCurriculum(
  courseId: string,
  userId: string,
  courseDocuments: any,
  userToken: string = '',
  onProgress?: (message: string) => void
) {
  const data = {
    course_id: courseId,
    course_documents: courseDocuments,
    user_id: userId,
  };

  return makeLongRunningRequest('courses/index_and_generate_curriculum', 'PUT', data, userToken, undefined, onProgress)
    .then(response => {
      if (!response.ok) {
        return response.json().then(errorInfo => {
          console.error('Error response from server:', errorInfo);
          throw new Error(`HTTP error! Status: ${response.status}`);
        });
      }
      return response.json();
    });
}

export function updateCurriculum(
  courseId: string,
  updatedCourseInfo: any,
  creationDone = false,
  userToken: string = ''
) {
  const data = {
    course_id: courseId,
    creation_done: creationDone,
    ...updatedCourseInfo
  };

  return makeProxyRequest('courses/', 'PUT', data, userToken)
    .then(response => {
      if (!response.ok) {
        return response.json().then(errorInfo => {
          console.error('Error response from server:', errorInfo);
          throw new Error(`HTTP error! Status: ${response.status}`);
        });
      }
      return response.json();
    });
}

export async function fetchCourseIntroMessage(
  courseId: string,
  generateNew: boolean,
  userToken: string = ''
) {
  const endpoint = `courses/gen_intro?course_id=${courseId}&generate_new=${generateNew}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export async function setCourseIntroMessage(
  courseId: string,
  message: string,
  userToken: string = ''
) {
  const data = { course_id: courseId, message: message };

  try {
    const response = await makeProxyRequest('courses/gen_intro', 'POST', data, userToken);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error('Error in setCourseIntroMessage API call:', error);
    throw error;
  }
}

export function sendMessageInCourse(
  data: CourseMessageRequest,
  userToken: string = '',
  onProgress?: (message: string) => void
): any {
  return makeLongRunningRequest('courses/instruct', 'POST', data, userToken, undefined, onProgress);
}

export const fetchCoursesByCreator = async (creatorId: string, userToken: string = '') => {
  const endpoint = `courses/list/${creatorId}`;

  return makeProxyRequest(endpoint, 'GET', undefined, userToken)
    .then((response) => {
      if (response.ok) {
        return response.json();
      }
    }, () => {})
    .then((courses) => {
      return (courses || []).filter(course => course?.creation_done === true);
    })
    .catch((error) => {
      console.error('Error in fetchCoursesByCreator API call:', error)
    })
};

export const fetchCourseResultsByStudent = async (studentId: string, userToken: string = '') => {
  const endpoint = `courses/results?student_id=${studentId}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined, userToken);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in fetchCourseResultsByStudent API call:', error);
    throw error;
  }
};

export const fetchResultsByStudentAndCourse = async (
  studentId: string,
  courseId: string,
  userToken: string = ''
) => {
  const endpoint = `courses/results?student_id=${studentId}&course_id=${courseId}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined, userToken);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in fetchResultsByStudentAndCourse API call:', error);
    throw error;
  }
}

export const fetchResultsByCourse = async (courseId: string, userToken: string = '') => {
  const endpoint = `courses/results?course_id=${courseId}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined, userToken);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in fetchResultsByCourse API call:', error);
    throw error;
  }
}

export const getCurriculumDetails = async (courseId: string, userToken: string = '') => {
  const endpoint = `courses/${courseId}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined, userToken);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return response.json();
  } catch (error) {
    console.error('Error in getCurriculumDetails API call:', error);
    throw error;
  }
};

export function deleteCourse(courseId: string, userToken: string = '') {
  const data = { course_id: courseId };

  return makeProxyRequest('courses/', 'DELETE', data, userToken)
    .then(response => {
      if (!response.ok) {
        return response.json().then(errorInfo => {
          console.error('Error response from server:', errorInfo);
          throw new Error(`HTTP error! Status: ${response.status}`);
        });
      }
      return response.json();
    })
    .catch(error => {
      console.error('Error in deleteCourse API call:', error);
      throw error;
    });
}

export async function fetchCourseProgress(roomId: string, userToken: string = ''): Promise<any> {
  const encodedRoomId = encodeURIComponent(roomId);
  const endpoint = `courses/progress?room_id=${encodedRoomId}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined, userToken);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching course progress:', error);
    throw error;
  }
}

export async function updateCourseResults(
  courseId: string,
  studentId: string,
  moduleNum: number,
  results: any,
  userToken: string = ''
): Promise<any> {
  const resultsData = {
    score: results.filter(result => result.isCorrect).length,
    questions: results.map(result => ({
      question: result.question,
      selectedOption: result.selectedOption,
      correctOption: result.correctOption,
      isCorrect: result.isCorrect
    })),
    module_num: moduleNum,
  };

  const data = {
    course_id: courseId,
    student_id: studentId,
    results: resultsData,
  };

  try {
    const response = await makeProxyRequest('courses/results', 'PUT', data, userToken);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Error updating course results:', error);
    throw error;
  }
}

export const uploadCourseBanner = async (
  course_id: string,
  unsplash_url: string,
  userToken: string = ''
) => {
  const proxyURL = '/.netlify/functions/formdata-proxy';

  const formData = new FormData();

  // Add proxy configuration
  formData.append('endpoint', 'upload_image');
  formData.append('userToken', userToken);
  // Note: uploadCourseBanner doesn't seem to need authUserId, but we can add it if needed
  // formData.append('authUserId', '');

  // Add actual form data
  formData.append('course_id', course_id);
  formData.append('unsplash_url', unsplash_url);

  const requestOptions = {
    method: 'POST',
    body: formData,
  };

  try {
    const response = await fetch(proxyURL, requestOptions);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    return;
  } catch (error) {
    console.error('Error in uploadCourseBanner API call:', error);
    throw error;
  }
};

export const searchUnsplash = async (
  query: string,
  page: number = 1,
  per_page: number = 10,
  orientation: 'landscape' | 'portrait' | 'squarish' = 'landscape'
) => {
  const proxyURL = '/.netlify/functions/unsplash-proxy';

  // Build query parameters
  const params = new URLSearchParams({
    query: query,
    page: page.toString(),
    per_page: per_page.toString(),
    orientation: orientation
  });

  try {
    const response = await fetch(`${proxyURL}?${params.toString()}`);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Unsplash API error: ${errorData.error || response.statusText}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error in searchUnsplash API call:', error);
    throw error;
  }
}

export function sendCourseCompletionEmail(
  email: string,
  user_name: string,
  course_name: string,
  teacher_name: string,
  courses_link: string,
  userToken: string = ''
): Promise<any> {
  const data = {
    email: email,
    user_name: user_name,
    course_name: course_name,
    teacher_name: teacher_name,
    courses_link: courses_link
  };

  return makeProxyRequest('send-course-completion-email', 'POST', data, userToken)
    .then(response => {
      if (!response.ok) {
        return response.json().then(err => {
          throw new Error(`HTTP error! status: ${response.status}, message: ${err.message}`);
        });
      }
      return response.json();
    })
}

////////////////////////////////////////////////////////////////////
// Monetization related functions
////////////////////////////////////////////////////////////////////

export async function createCheckoutSession(
  user_id: string,
  email: string,
  item: string,
  customerId?: string,
  itemId?: string,
  redirectURL?: string,
  isPremium?: boolean,
  trialPeriod?: number,
  referral?: string,
  userToken: string = ''
) {
  let origin = window.location.origin + '/';

  const data = {
    'email': email,
    'item': item,
    'origin_domain': origin,
  };

  if (customerId) data['customer'] = customerId;
  if (itemId) data['item_id'] = itemId;
  if (redirectURL) data['redirect_url'] = redirectURL;
  if (isPremium) data['is_premium'] = true;
  if (trialPeriod) data['trial_period'] = trialPeriod;
  if (referral) data['referral'] = referral;

  return makeProxyRequest(`payments/checkout-session/${user_id}`, 'POST', data, userToken);
}

export async function getPaymentsHistory(
  user_id: string,
  page?: number,
  page_size?: number,
  userToken: string = ''
) {
  page ||= 1;
  page_size ||= 10;
  const endpoint = `payments/history/${user_id}?page=${page}&pageSize=${page_size}`;

  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export async function getCheckoutSessionStatus(
  user_id: string,
  session_id: string,
  userToken: string = ''
) {
  const endpoint = `payments/checkout-session/${user_id}?session_id=${session_id}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export async function getPrices(items: string[], userToken: string = '') {
  let itemsString = "[" + items.join(", ") + "]";
  const endpoint = `payments/prices?items=${itemsString}`;

  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export async function getRevenue(
  user_id: string,
  product_id: string,
  month: number,
  year: number,
  userToken: string = ''
) {
  const endpoint = `payments/revenue/${user_id}?product_id=${product_id}&month=${month}&year=${year}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export async function getSubscriptions(user_id: string, userToken: string = '') {
  const endpoint = `payments/subscriptions/${user_id}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export async function setCustomPrice(
  user_id: string,
  mode: ('create' | 'update'),
  name?: string,
  description?: string,
  price?: number,
  interval?: string,
  prod_id?: string,
  userToken: string = ''
) {
  const data = { 'user_id': user_id };

  if (name) data['name'] = name;
  if (description) data['description'] = description;
  if (price || price === 0) data['price'] = price;
  if (interval) data['interval'] = interval;
  if (prod_id) data['product_id'] = prod_id;

  const method = mode === 'create' ? 'POST' : 'PUT';
  return makeProxyRequest('payments/prices', method, data, userToken);
}

////////////////////////////////////////////////////////////////////

export async function indexPrefetch(user_id: string, userToken: string = '') {
  const endpoint = `index_prefetch?user_id=${user_id}`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export async function getTwitterAuthUrl(userToken: string = ''): Promise<string> {
  const response = await makeProxyRequest('twitter/auth-url', 'GET', undefined, userToken);

  if (!response.ok) {
    throw new Error('Failed to fetch Twitter auth URL');
  }

  const data = await response.json();
  return data.url;
}

export async function handleTwitterCallback(code: string, userId: string, userToken: string = ''): Promise<any> {
  const endpoint = `twitter/callback?code=${code}&user_id=${userId}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined, userToken);

    if (!response.ok) {
      throw new Error('Failed to fetch Twitter user data');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error handling Twitter callback:', error);
    throw error;
  }
}

export async function fetchTwitterData(userId: string, userToken: string = ''): Promise<any> {
  const endpoint = `twitter/data/${userId}`;

  return makeProxyRequest(endpoint, 'GET', undefined, userToken)
    .then((response) => {
      if (response.ok) {
        return response.json();
      }
    }, () => {
      return {is_twitter_authenticated: false}
    })
    .catch((err) => {
      console.error('Error in fetchTwitterData, ', err)
      return {is_twitter_authenticated: false}
    })
}

export async function handleSaveJournalChat(
  userId: string,
  journalTitle: string,
  journalText: string,
  userToken: string = '',
  onProgress?: (message: string) => void
): Promise<any> {
  try {
    const data = {
      user_id: userId,
      message: journalText,
      title: journalTitle,
      room_id: `${userId}.bot.journal`,
    };

    const response = await makeLongRunningRequest('journal/chat', 'POST', data, userToken, undefined, onProgress);
    console.log(response)
    return response.ok;
  }
  catch (error) {
    console.log("Journal Save error: ", error)
  }
}

export const fetchSearchResponseData = async (query: string, userToken: string = '') => {
  const endpoint = `/moe/search?query=${query}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined, userToken);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in fetchSearchResponseData API call:', error);
    throw error;
  }
};

export const fetchRandomCreator = async (userToken: string = '') => {
  const endpoint = `/moe/im_feeling_lucky`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined, userToken);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }
    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in fetchSearchResponseData API call:', error);
    throw error;
  }
};

export const getSearchSummaryData = async (responses: any, userToken: string = '') => {
  const data = { "responses": responses };

  try {
    const response = await makeProxyRequest('/moe/summarize', 'POST', data, userToken);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in getSearchSummaryData API call:', error);
    throw error;
  }
};

export async function fetchTprCalcSheetData(): Promise<string[][] | null> {
  const proxyURL = '/.netlify/functions/google-sheets-proxy';

  try {
    // Use default sheetId for Frank's TPR calculator
    const params = new URLSearchParams({ sheetId: 'frank_tpr_calc' });
    const response = await fetch(`${proxyURL}?${params.toString()}`);

    if (!response.ok) {
      const errorData = await response.json();
      console.warn(`Failed to fetch sheet data: ${errorData.error || response.statusText}`);
      return null;
    }

    const data = await response.json();

    // Return the values array for backward compatibility with existing component usage
    return data.values || null;
  } catch (error) {
    console.error('Error fetching Google Sheet data:', error);
    return null;
  }
}

export const handleGoogleDriveResync = async (
  userId: string,
  link: string[],
  userToken: string = '',
  onProgress?: (message: string) => void
) => {
  const data = {
    user_id: userId,
    google_drive_folder_link: link,
    return_user_doc: true
  }

  if (!link.length || !(process.env.REACT_APP_ENABLE_GOOGLE_DRIVE_INTEGRATION === 'true')) {
    return Promise.resolve();
  }

  return makeLongRunningRequest('google-drive/sync-google-drive-folder', 'POST', data, userToken, undefined, onProgress)
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return response.json();
    })
    .catch(error => {
      console.error('Error in Google Drive Resync API call:', error);
      throw error;
    })
};

export const getGoogleDriveResyncUpdates = async (
  userId: string,
  link: string,
  userToken: string = ''
) => {
  const endpoint = `google-drive/index-progress?user_id=${userId}&&google_drive_folder_link=${link}`;

  try {
    const response = await makeProxyRequest(endpoint, 'GET', undefined, userToken);
    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const results = await response.json();
    return results;
  } catch (error) {
    console.error('Error in Google Drive Resync Update API call:', error);
    throw error;
  }
};

export const fetchFeaturedCreators = async (userToken: string = '') => {
  const endpoint = `api/featured/creators`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export const fetchFeaturedCourses = async (userToken: string = '') => {
  const endpoint = `api/featured/courses`;
  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
}

export const fetchFanList = async (
  creator_id: string,
  num_fans?: number,
  userToken: string = ''
) => {
  num_fans ||= 10;
  const endpoint = `api/get_fan_list?creator_id=${creator_id}&num_fans=${num_fans}`;

  return makeProxyRequest(endpoint, 'GET', undefined, userToken)
    .then(async (resp) => {
      if (resp.ok) {
        return resp.json()
      } else {
        return []
      }
    })
    .catch(err => {
      console.log(err)
      return []
    })
}

export function getExistingJournalEntries(
  userId: string,
  page?: number,
  pageSize?: number,
  userToken: string = ''
): any {
  page ||= 1;
  pageSize ||= 10;
  const endpoint = `journal/get_history?user_id=${userId}&page=${page}&per_page=${pageSize}`;

  return makeProxyRequest(endpoint, 'GET', undefined, userToken);
};

export async function handleUpdateJournalChat(
  userId: string,
  journalTitle: string,
  journalText: string,
  journal_id: string,
  userToken: string = ''
): Promise<any> {
  try {
    const data = {
      user_id: userId,
      data: {
        message: journalText,
        title: journalTitle,
      },
      journal_id: journal_id,
    };

    const response = await makeProxyRequest('journal/update_entry', 'PUT', data, userToken);
    console.log(response)
    return response.ok;
  }
  catch (error) {
    console.log("Journal Save error: ", error)
  }
}

export async function handleDeleteJournalChat(
  userId: string,
  journalId: string,
  userToken: string = ''
): Promise<any> {
  try {
    const data = {
      user_id: userId,
      journal_id: journalId,
    };

    const response = await makeProxyRequest('journal/delete_entry', 'DELETE', data, userToken);
    console.log(response)
    return response.ok;
  }
  catch (error) {
    console.log("Journal Delete error: ", error)
  }
}


export function configureMeeting(
  meetingId: string,
  userIds: string[],
  userToken: string = ''
): any {
  const data = {
    meeting_id: meetingId,
    user_ids: userIds,
  };

  return makeProxyRequest('meetings/configure', 'POST', data, userToken);
}
